import { useTranslation } from 'react-i18next'
import { cn } from '@ludus/ui'
import type { InternalStatus } from '@ludus/core'
import type { StageMetrics } from '../../types.ts'
import { getStageColor, type StageKind } from '@/shared/utils/stageColors.ts'

interface StageCardProps {
  index: number
  totalStages: number
  stage: InternalStatus
  metrics: StageMetrics
  selected: boolean
  /**
   * Full-colour fill without the structural "selected" treatment. Set for every
   * card in the all-leads view (nothing focused) so the row reads as live colour
   * rather than a greyed-out "nothing picked" state.
   */
  vivid?: boolean
  /** This stage's totalValue divided by the pipeline-wide max — drives the bar height. */
  valueShare: number
  showValue: boolean
  /** «12 deals», in the board's own words. */
  countLabel: string
  /** Optional lifecycle kind. When omitted, palette falls back to position-based bucket. */
  kind?: StageKind
  onClick: () => void
}

const compactValue = new Intl.NumberFormat('en-EU', {
  style: 'currency',
  currency: 'EUR',
  notation: 'compact',
  maximumFractionDigits: 0,
})

const BAR_MIN = 22
const BAR_MAX = 78
const SELECTED_BUMP = 14

export function StageCard({
  index,
  totalStages,
  stage,
  metrics,
  selected,
  vivid = false,
  valueShare,
  showValue,
  countLabel,
  kind,
  onClick,
}: StageCardProps) {
  const { t } = useTranslation()
  const colors = getStageColor(index, totalStages, kind)
  const baseHeight = BAR_MIN + Math.round(valueShare * (BAR_MAX - BAR_MIN))
  const barHeight = baseHeight + (selected ? SELECTED_BUMP : 0)

  // `intake` and `lost` deserve a flatter, less-luminous look even when selected — they
  // shouldn't compete with the warm/won "money" stages for attention.
  const isQuiet = kind === 'intake' || kind === 'lost'

  // Full-colour fill on selection OR in the all-leads view (`vivid`). Only the
  // colour responds to `vivid`; the height bump / shadow / underline stay tied to
  // `selected`, so all-leads reads as "all live" without faking a selection.
  const fill =
    selected || vivid
      ? `linear-gradient(180deg, ${colors.active} 0%, ${colors.base} 100%)`
      : `linear-gradient(180deg, ${colors.base} 0%, color-mix(in oklab, ${colors.base} 60%, #000) 100%)`

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      title={
        selected
          ? `${stage.name} · ${t('pages.pipelines.stage.showAllHint', { defaultValue: 'Click to show all leads' })}`
          : [
              stage.name,
              countLabel,
              ...(showValue ? [compactValue.format(metrics.totalValue)] : []),
            ].join(' · ')
      }
      className={cn(
        'group relative flex h-full w-full flex-col items-stretch justify-end gap-1.5 pb-1.5 text-left transition-transform',
        'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'hover:-translate-y-0.5',
      )}
    >
      <div className="flex flex-1 items-end px-0.5">
        <span
          aria-hidden="true"
          className={cn(
            'block w-full rounded-md transition-[height,box-shadow]',
            selected
              ? isQuiet
                ? 'shadow-[inset_0_1px_0_rgba(255,255,255,0.10),inset_0_-1px_0_rgba(0,0,0,0.35)]'
                : 'shadow-[inset_0_1px_0_rgba(255,255,255,0.18),inset_0_-1px_0_rgba(0,0,0,0.35),0_8px_18px_-10px_rgba(0,0,0,0.65)]'
              : 'shadow-[inset_0_1px_0_rgba(255,255,255,0.06),inset_0_-1px_0_rgba(0,0,0,0.35)]',
          )}
          style={{ height: `${barHeight}px`, background: fill }}
        />
      </div>

      <div className="flex flex-col gap-0.5 px-0.5">
        <span className="truncate font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
          {String(index + 1).padStart(2, '0')} · {stage.name}
        </span>
        <span className="flex items-baseline gap-1.5 text-[12px] tabular-nums">
          <span className={cn('font-bold', selected ? 'text-foreground' : 'text-foreground/85')}>
            {metrics.leadCount}
          </span>
          {showValue && (
            <span className="text-foreground/60">{compactValue.format(metrics.totalValue)}</span>
          )}
          {metrics.stuckCount > 0 && (
            <span
              className="ml-auto font-mono font-semibold text-toast-error-text"
              title={`${metrics.stuckCount} stuck`}
            >
              −{metrics.stuckCount}
            </span>
          )}
        </span>
      </div>

      {/*
        Bottom underline strip — ties the row of cards visually. Defaults to a soft
        ~50% trace tinted with the stage colour; selected stages light up to full.
      */}
      <span
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-[3px] rounded-full transition-opacity"
        style={{
          background: colors.active,
          opacity: selected ? 1 : vivid ? 0.8 : 0.5,
        }}
      />
    </button>
  )
}
